import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import type { Categoria, Sucursal } from '../types/relacionLaboral';

/**
 * Sucursales de una empresa. Si la empresa no fue seleccionada, no fetcha.
 */
export function useSucursales(empresaId: string | null | undefined) {
  return useQuery({
    queryKey: ['sucursales', empresaId],
    queryFn: async () => {
      if (!empresaId) return [];
      const { data, error } = await supabase
        .from('sucursales')
        .select('id, empresa_id, denominacion, domicilio, telefono, casa_central, activa')
        .eq('empresa_id', empresaId)
        .eq('activa', true)
        .order('casa_central', { ascending: false })
        .order('denominacion');
      if (error) throw error;
      return (data ?? []) as Sucursal[];
    },
    enabled: !!empresaId,
    staleTime: 5 * 60_000,
  });
}

/**
 * Catalogo global de categorias laborales (convenio sanidad).
 */
export function useCategorias() {
  return useQuery({
    queryKey: ['categorias'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categorias')
        .select('id, codigo, descripcion, activa')
        .eq('activa', true)
        .order('codigo');
      if (error) throw error;
      return (data ?? []) as Categoria[];
    },
    staleTime: 10 * 60_000,
  });
}

/**
 * Lista basica de empresas activas para usar en selects.
 * Devuelve solo id, cuit, razon_social (ahorra payload).
 */
export function useEmpresasActivas() {
  return useQuery({
    queryKey: ['empresas', 'activas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('empresas')
        .select('id, cuit, razon_social')
        .eq('estado', 'activa')
        .order('razon_social');
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; cuit: string; razon_social: string }>;
    },
    staleTime: 60_000,
  });
}
